import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import { ComponentIds } from 'src/app/models/component-ids';
import {
  Filter,
  FilterBuilderOutput,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import { PagedFeeRecipientReportResponse } from 'src/app/models/financial-reports';
import { ColumnOption } from 'src/app/models/grid-settings';
import {
  SystemService,
  Utilities,
  WorkflowContextService
} from 'src/app/services';

@Component({
  selector: 'app-financial-fee-recipients',
  templateUrl: './financial-fee-recipients.component.html',
  styleUrls: ['./financial-fee-recipients.component.css'],
  standalone: false
})
export class FinancialFeeRecipientsComponent implements OnInit, OnChanges {
  id = ComponentIds.FEE_RECIPIENTS;
  title = 'Fee-Recipients';
  loading = true;
  errorMessage = '';
  report: PagedFeeRecipientReportResponse;
  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];
  params: FilterBuilderParam[] = [
    {
      name: 'Recipient',
      id: 'recipient'
    },
    {
      name: 'Payment Type',
      id: 'paymentMethod',
      types: ['is', 'in'],
      options: ['Online', 'CreditCardPOS', 'Check', 'Cash']
    },
    {
      name: 'Confirmed Date',
      id: 'timeframe',
      inputType: 'date',
      types: ['range']
    }
  ];
  filters: FilterBuilderOutput = {};
  public useExcelFormat: boolean;

  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[] = [];

  rows: any[];
  public page = {
    size: 10,
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0
  };
  public sortField: string;
  public sortDescending = false;

  totals: any[];
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-report-fees-recipient-janus';
  constructor(
    private systemSvc: SystemService,
    private toast: ToastrService,
    private _context: WorkflowContextService,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.page.size = pageSize;
      }
    }

    this.columns = [
      {
        key: 'recipient',
        label: 'Recipient',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'payments',
        label: 'Payments',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'amount',
        label: 'Amount',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'jurisdiction',
        label: 'Jurisdiction',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      }
    ];

    this.columnOptions = this.columns.map(c => ({
      key: c.key,
      name: c.key,
      label: c.label,
      checked: !c.hidden,
      includeInExport: c.showExportOption ?? true
    }));

    this.setAvailableColumns();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  getQuery() {
    const filters: Filter[] = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }
    return {
      clientId: this._context.client.id,
      filters,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };
  }

  private getPagedQuery() {
    const filters: Filter[] = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (event.sortField === null) {
      // reset to default sorting
      this.sortField = 'recipient'; // choose your default
      this.sortDescending = false;
    } else {
      this.sortField = event.sortField;
      this.sortDescending = event.sortDescending ?? false;
    }
    this.page.pageNumber = 0;
    this.getReport();
  }

  onRetry() {
    this.getReport();
  }

  getReport() {
    const query = this.getPagedQuery();
    this.loading = true;
    this.systemSvc.getPagedFeeRecipientReport(query).subscribe({
      next: report => {
        this.page.totalElements = report.recordCount;
        this.page.totalPages = Math.ceil(report.recordCount / this.page.size);
        this.rows = report.items.map(i => {
          i['recipient'] = i.recipientName;
          i['payments'] = i.payments;
          i['amount'] = Utilities.formatCurrency(i.amount);
          i['jurisdiction'] = i.clientName;
          return i;
        });
        this.report = report;
        this.totals = [
          { label: 'Payments', value: this.report?.totalPayments },
          { label: 'Amount', value: `$${this.report?.totalAmount}` }
        ];
        this.loading = false;
      },
      error: err => {
        console.error(err);
        this.loading = false;
        this.errorMessage = 'Failed to load report. Please try again.';
      }
    });
  }

  onPageChange(page: number) {
    this.page.pageNumber = page;
    this.getReport();
  }

  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));

    this.page.size = pageSize;
    this.page.pageNumber = 0;
    this.getReport();
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.systemSvc.exportFeeRecipientReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `fee-recipient-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `fee-recipient-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }

  onExportColumnsChange(cols: string[]) {
    this.exportColumns = cols;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getReport();
  }

  onFiltersChanged(filters: FilterBuilderOutput) {
    this.filters = filters;
    this.page.pageNumber = 0; // reset to first page
    this.getReport();
  }

  onColumnsChanged(columnSet: { columns: any[]; exportColumns: any[] }) {
    // Update visible columns
    const visibleKeys = columnSet.columns.map(c => c.name);

    this.columns = this.availableColumns.map(col => ({
      ...col,
      hidden: !visibleKeys.includes(col.key)
    }));

    // Update export columns
    this.exportColumns = columnSet.exportColumns.map(c => c.name);
  }
}
